/**
 * Finance → Control Presupuestario → Creators / Influencers: el modelo de
 * cálculo, puro (sin red ni React). El backend entrega las filas agregadas
 * (`CreatorsDashboardData`) y esto devuelve lo que dibuja cada bloque: KPIs,
 * serie mensual, mix de oferta, segmento, región y clientes, en un período (un
 * mes o el año) y, si se pide, comparado con otro período.
 *
 * Reglas (las del dashboard de Finance, 07/10/2026):
 *  - Influencers = oferta 'IM'; Creators = el resto (el loader ya lo separa).
 *  - Clientes agrupados por holding con `HOLDING_RULES` (Mercado Libre + Mercado
 *    Pago, Unilever + Axe/Dove/…, Kimberly Clark + Huggies, Didi *, Beiersdorf
 *    + Nivea/Eucerin, CCU). Todo lo demás queda con su nombre.
 *  - Costo / Revenue = spend del período ÷ revenue de Content (Actual) del mismo
 *    período y la misma clave (segmento, región o holding). Sin revenue → null.
 *  - "Clientes activos" = empresas distintas (company_name) con spend.
 *
 * Tests: `npm run test:finance-creators` (backend).
 */

import { foldKey } from './text-normalize';
import { CREATORS_BUCKETS, type CreatorsBucket, type CreatorsDashboardData, type CreatorsKind, type CreatorsSpendRow } from './types/finance-creators';

export type CreatorsPeriod = number | 'all';
export type CreatorsDim = 'segment' | 'region' | 'client';

/** Sin segmento / región cargados en la planilla. */
export const NO_DATA = '(sin dato)';

/* ───────────────────────── holdings ───────────────────────── */

/**
 * Holding → patrones sobre `foldKey(nombre)`. Palabra completa (`\b`) para que
 * "axe" o "dove" no peguen dentro de otro nombre. Agregar un holding = sumar una
 * fila acá (y su caso en el test).
 */
export const HOLDING_RULES: ReadonlyArray<readonly [string, RegExp]> = [
  ['Mercado Libre', /\bmercado ?(libre|pago|play)\b|\bmercadopago\b|\bmercadolibre\b/],
  ['Unilever', /\bunilever\b|\baxe\b|\bdove\b|\brexona\b|\bhellmann|\ben ?combo\b/],
  ['Kimberly Clark', /\bkimberly\b|\bhuggies\b/],
  ['Didi', /^didi\b/],
  ['Beiersdorf', /\bbeiersdorf\b|\bnivea\b|\beucerin\b/],
  ['CCU', /^ccu\b/],
];

/** Conectores que van en minúscula al prolijar un nombre en mayúsculas ("PALACIO DE HIERRO" → "Palacio de Hierro"). */
const PARTICLES = new Set(['de', 'del', 'la', 'las', 'los', 'el', 'y', 'e', 'da', 'do']);

/**
 * "PARQUE ARAUCO" → "Parque Arauco". Palabras de hasta 3 letras quedan en
 * mayúsculas (CCU, KFC, LG), salvo los conectores. Un nombre que ya trae
 * minúsculas se respeta, con la primera letra en mayúscula ("scotiabank (TT)").
 */
function prettyName(raw: string): string {
  const v = raw.replace(/\s+/g, ' ').trim();
  if (!v) return NO_DATA;
  if (/[a-z]/.test(v)) return v.charAt(0).toUpperCase() + v.slice(1);
  return v
    .split(' ')
    .map((w, i) => {
      const lower = w.toLowerCase();
      if (i > 0 && PARTICLES.has(lower)) return lower;
      return w.replace(/[^A-Za-zÀ-ÿ]/g, '').length <= 3 ? w : w.charAt(0) + w.slice(1).toLowerCase();
    })
    .join(' ');
}

/** El holding (o el nombre prolijo) de un cliente. */
export function holdingOf(name: string | null | undefined): string {
  const key = foldKey(name);
  if (!key) return NO_DATA;
  for (const [holding, re] of HOLDING_RULES) if (re.test(key)) return holding;
  return prettyName(name ?? '');
}

/* ───────────────────────── períodos ───────────────────────── */

export function periodMonths(period: CreatorsPeriod, lastMonth: number): number[] {
  if (period === 'all') return Array.from({ length: lastMonth }, (_, i) => i + 1);
  return period >= 1 && period <= lastMonth ? [period] : [];
}

function rowsOf(data: CreatorsDashboardData, kind: CreatorsKind, months: number[]): CreatorsSpendRow[] {
  const set = new Set(months);
  return data.spend.filter((r) => r.kind === kind && set.has(r.month));
}

/** Meses con spend > 0 de un tipo (Influencers arranca en marzo). */
export function activeMonths(data: CreatorsDashboardData, kind: CreatorsKind): number[] {
  const out = new Set<number>();
  for (const r of data.spend) if (r.kind === kind && r.usd > 0) out.add(r.month);
  return [...out].sort((a, b) => a - b);
}

/* ───────────────────────── KPIs ───────────────────────── */

export interface CreatorsKpis {
  spend: number;
  n: number;
  clients: number;
  /** Año: spend / meses con actividad. Mes: % del spend del año. */
  avg: number;
}

export function computeCreatorsKpis(data: CreatorsDashboardData, kind: CreatorsKind, period: CreatorsPeriod): CreatorsKpis {
  const rows = rowsOf(data, kind, periodMonths(period, data.lastMonth));
  const spend = rows.reduce((a, r) => a + r.usd, 0);
  const n = rows.reduce((a, r) => a + r.n, 0);
  const clients = new Set(rows.filter((r) => r.usd > 0 && foldKey(r.company)).map((r) => foldKey(r.company))).size;
  let avg: number;
  if (period === 'all') {
    const act = activeMonths(data, kind).length;
    avg = act ? spend / act : 0;
  } else {
    const year = rowsOf(data, kind, periodMonths('all', data.lastMonth)).reduce((a, r) => a + r.usd, 0);
    avg = year ? (spend / year) * 100 : 0;
  }
  return { spend, n, clients, avg };
}

/* ───────────────────────── serie mensual ───────────────────────── */

export interface CreatorsMonthPoint {
  month: number;
  total: number;
  /** Solo Creators: spend por bucket de oferta. */
  buckets: Record<CreatorsBucket, number>;
}

export function computeMonthly(data: CreatorsDashboardData, kind: CreatorsKind): CreatorsMonthPoint[] {
  const out = periodMonths('all', data.lastMonth).map((month) => ({
    month,
    total: 0,
    buckets: Object.fromEntries(CREATORS_BUCKETS.map((b) => [b, 0])) as Record<CreatorsBucket, number>,
  }));
  for (const r of data.spend) {
    if (r.kind !== kind) continue;
    const p = out[r.month - 1];
    if (!p) continue;
    p.total += r.usd;
    if (r.bucket) p.buckets[r.bucket] += r.usd;
  }
  return out;
}

/* ───────────────────────── tablas ───────────────────────── */

export interface CreatorsRow {
  name: string;
  spend: number;
  n: number;
  /** % del spend del período. */
  pct: number;
  /** Revenue de Content del mismo período y clave; null si no hay. */
  revenue: number | null;
  /** Costo / Revenue en %; null sin revenue. */
  ratio: number | null;
}

function keyOf(dim: CreatorsDim, r: { segment: string; region: string; company?: string; client?: string }): string {
  if (dim === 'segment') return r.segment?.trim() || NO_DATA;
  if (dim === 'region') return r.region?.trim() || NO_DATA;
  return holdingOf(r.company ?? r.client);
}

/** Acumulador por clave `foldKey`: "Retail" y "retail" son una sola fila (gana el primer nombre visto). */
type Acc = Map<string, { name: string; spend: number; n: number }>;

function add(acc: Acc, name: string, usd: number, n: number): void {
  const k = foldKey(name);
  const cur = acc.get(k) ?? { name, spend: 0, n: 0 };
  cur.spend += usd;
  cur.n += n;
  acc.set(k, cur);
}

function finish(acc: Acc, revenue: Map<string, number> | null): CreatorsRow[] {
  // Los ajustes negativos (notas de crédito) también se muestran: si no, el % de las demás no cierra con el KPI.
  const total = [...acc.values()].reduce((a, v) => a + v.spend, 0);
  return [...acc.entries()]
    .filter(([, v]) => Math.abs(v.spend) >= 0.5)
    .map(([key, v]) => {
      const rev = revenue ? revenue.get(key) ?? null : null;
      return {
        name: v.name,
        spend: v.spend,
        n: v.n,
        pct: total ? (v.spend / total) * 100 : 0,
        revenue: rev && rev > 0 ? rev : null,
        ratio: rev && rev > 0 ? (v.spend / rev) * 100 : null,
      };
    })
    .sort((a, b) => b.spend - a.spend);
}

/** Spend por segmento, región o holding en un período, con el revenue de Content de la misma clave. */
export function computeBreakdown(data: CreatorsDashboardData, kind: CreatorsKind, dim: CreatorsDim, period: CreatorsPeriod): CreatorsRow[] {
  const months = periodMonths(period, data.lastMonth);
  const acc: Acc = new Map();
  for (const r of rowsOf(data, kind, months)) add(acc, keyOf(dim, r), r.usd, r.n);
  const set = new Set(months);
  const revenue = new Map<string, number>();
  for (const r of data.revenue) {
    if (!set.has(r.month)) continue;
    const k = foldKey(keyOf(dim, r));
    revenue.set(k, (revenue.get(k) ?? 0) + r.usd);
  }
  return finish(acc, revenue);
}

/** Mix de oferta de Creators (sin revenue: la oferta no existe del lado de Capacity). */
export function computeOffer(data: CreatorsDashboardData, period: CreatorsPeriod): CreatorsRow[] {
  const acc: Acc = new Map();
  for (const r of rowsOf(data, 'creators', periodMonths(period, data.lastMonth))) add(acc, r.bucket ?? 'Otros', r.usd, r.n);
  const rows = finish(acc, null);
  // Orden fijo de los buckets (el del gráfico), no por dólares.
  return CREATORS_BUCKETS.map((b) => rows.find((r) => r.name === b)).filter((r): r is CreatorsRow => !!r);
}

/* ───────────────────────── comparar períodos ───────────────────────── */

export interface CreatorsCompareRow {
  name: string;
  a: number;
  b: number;
  /** (b − a) / a en %; null si `a` es 0 (no hay base contra la cual medir). */
  deltaPct: number | null;
}

export function deltaPct(a: number, b: number): number | null {
  if (!a) return b ? null : 0;
  return ((b - a) / Math.abs(a)) * 100;
}

/** Cruza dos listas por nombre; ordena por el volumen de los dos períodos juntos. */
export function compareRows(rowsA: Array<{ name: string; spend: number }>, rowsB: Array<{ name: string; spend: number }>): CreatorsCompareRow[] {
  const map = new Map<string, CreatorsCompareRow>();
  for (const r of rowsA) map.set(foldKey(r.name), { name: r.name, a: r.spend, b: 0, deltaPct: null });
  for (const r of rowsB) {
    const k = foldKey(r.name);
    const cur = map.get(k) ?? { name: r.name, a: 0, b: 0, deltaPct: null };
    cur.b = r.spend;
    map.set(k, cur);
  }
  return [...map.values()]
    .map((r) => ({ ...r, deltaPct: deltaPct(r.a, r.b) }))
    .filter((r) => r.a !== 0 || r.b !== 0)
    .sort((x, y) => y.a + y.b - (x.a + x.b));
}
